import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminLogin } from '../../services/api';
import logo from '../../assets/bleums-logo.png';

// Why the admin was sent back here, set by the axios interceptors.
const BOUNCE_MESSAGES: Record<string, string> = {
  expired: 'Your session expired. Please sign in again.',
  forbidden: "That account isn't an admin. Sign in with an admin account.",
};

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  // Explain the bounce instead of showing an empty form with no context.
  useEffect(() => {
    const reason = sessionStorage.getItem('adminAuthReason');
    if (reason) {
      setNotice(BOUNCE_MESSAGES[reason] ?? 'Please sign in again.');
      sessionStorage.removeItem('adminAuthReason');
    }
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const data = await adminLogin({ username, password });
      // Stash id_token (carries Cognito groups) for admin endpoints
      login(data.id_token ?? data.access_token, { username });

      // Prefer where they were headed: router state when ProtectedRoute
      // redirected, sessionStorage when an interceptor did.
      const fromState = (location.state as { from?: { pathname: string } })?.from?.pathname;
      const fromStorage = sessionStorage.getItem('adminReturnTo');
      sessionStorage.removeItem('adminReturnTo');
      const target = fromState ?? fromStorage;
      navigate(target && target.startsWith('/admin') ? target : '/admin', { replace: true });
    } catch (e: any) {
      const status = e.response?.status;
      if (status === 401 || status === 404) {
        // Don't echo Cognito's "User not found" — it tells an attacker
        // which addresses exist.
        setError('Incorrect email or password.');
      } else if (status === 503) {
        setError('Sign-in is unavailable right now. Please try again shortly.');
      } else {
        setError(e.response?.data?.detail ?? 'Sign-in failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <form onSubmit={onSubmit} className="w-full max-w-sm bg-white rounded shadow-sm p-6 space-y-4">
        <img src={logo} alt="Bleums Creations" className="h-24 w-auto object-contain mx-auto" />
        <h1 className="text-xl font-semibold text-primary text-center">Admin sign in</h1>
        {notice && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {notice}
          </div>
        )}
        <div>
          <label className="block text-xs text-text-light mb-1">Email</label>
          <input
            required
            type="email"
            autoComplete="username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            className="w-full border border-background-dark rounded px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-text-light mb-1">Password</label>
          <input
            required
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full border border-background-dark rounded px-3 py-2 text-sm"
          />
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
};

export default AdminLogin;
